"use client";

import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { FC, useCallback, useState } from "react";

import Button from "@/components/ui/button";
import { useAuth } from "@/contexts/auth_context";
import { PostStatus, PostWithForecasts } from "@/types/post";
import { getPostEditLink } from "@/utils/navigation";

import { submitPostForReview } from "../../actions";

const PostSubmissionPanel: FC<{
  post: PostWithForecasts;
}> = ({ post }) => {
  const t = useTranslations();
  const router = useRouter();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const credits = user?.submission_credits;
  const staffBypass = !!(user?.is_staff || user?.is_superuser);
  const hasCredits = staffBypass || (credits?.balance ?? 0) > 0;
  const submitDisabled = isSubmitting || !hasCredits;
  const editDisabled =
    hasSubmitted || post.curation_status !== PostStatus.DRAFT;
  const editLink = getPostEditLink(post);
  const editButton = (
    <Button
      type="button"
      variant="tertiary"
      disabled={editDisabled}
      onClick={() => {
        if (editDisabled) return;
        router.push(editLink);
      }}
    >
      {t("edit")}
    </Button>
  );

  const handleSubmitQuestion = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await submitPostForReview(post.id);
      setHasSubmitted(true);
    } catch (e) {
      console.error("Failed to submit question for review:", e);
      setError(
        `Failed to submit: ${e instanceof Error ? e.message : String(e)}`
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [post.id]);

  if (hasSubmitted) {
    return (
      <div className="mb-5 lg:mb-6">
        <div className="rounded border border-green-400 bg-green-50 p-4 dark:border-green-600 dark:bg-green-900/20">
          <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{t("questionUndergoingAutoValidation")}</span>
          </div>
          <div className="mt-3 flex gap-2">{editButton}</div>
        </div>
      </div>
    );
  }

  if (post.curation_status === PostStatus.DRAFT) {
    return (
      <div className="mb-5 lg:mb-6">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            disabled={submitDisabled}
            onClick={handleSubmitQuestion}
          >
            {t("submitQuestion")}
          </Button>
          {editButton}
        </div>
        {/* Submit is disabled with no credits — say why, or it reads as broken. */}
        {!staffBypass && !hasCredits && (
          <p className="mt-2 text-xs text-red-700 dark:text-red-400">
            {t("noSubmissionCredits")}
          </p>
        )}
        {error && (
          <div className="mt-2 flex items-center gap-2 text-xs text-red-700 dark:text-red-400">
            <FontAwesomeIcon icon={faTimesCircle} />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  if (post.curation_status === PostStatus.PENDING) {
    return (
      <div className="mb-5 lg:mb-6">
        <div className="rounded border border-green-400 bg-green-50 p-4 dark:border-green-600 dark:bg-green-900/20">
          <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{t("questionUndergoingAutoValidation")}</span>
          </div>
          <div className="mt-3 flex gap-2">{editButton}</div>
        </div>
      </div>
    );
  }

  if (post.curation_status === PostStatus.UNDER_APPEAL) {
    return (
      <div className="mb-5 lg:mb-6">
        <div className="rounded border border-green-400 bg-green-50 p-4 dark:border-green-600 dark:bg-green-900/20">
          <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{t("questionUndergoingManualReview")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (
    post.curation_status === PostStatus.APPROVED &&
    post.open_time &&
    Date.parse(post.open_time) > Date.now()
  ) {
    return (
      <div className="mb-5 lg:mb-6">
        <div className="rounded border border-green-400 bg-green-50 p-4 dark:border-green-600 dark:bg-green-900/20">
          <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{t("questionApproved")}</span>
          </div>
          <div className="mt-3 flex gap-2">{editButton}</div>
        </div>
      </div>
    );
  }

  return null;
};

export default PostSubmissionPanel;
